import { type ReactNode } from 'react';
import { graphql } from 'gatsby';
import { ImageElement } from '@kontent-ai/gatsby-components';
import useLinkProps from '@/components/Link/hooks/useLinkProps';
import pushInDataLayer from '@/utils/pushInDataLayer';
import { Button, IconWrapper, type LoginLinkSize } from './LoginLink.styles';

const LARGE_ICON_HEIGHT = 36;
const LARGE_ICON_WIDTH = 36;
const SMALL_ICON_HEIGHT = 16;
const SMALL_ICON_WIDTH = 16;

const LoginLink = ({
  elements: {
    link: { value: linkValue },
    icon: { value: iconValue },
    method,
  },
  size = 'large',
}: Queries.LOGIN_LINKFragment & { size?: LoginLinkSize }) => {
  const linkText = linkValue.linkElements.text.value;
  const isPostMethod = method.value === 'post';
  const { linkChildren, linkProps } = useLinkProps({
    action: linkValue.linkElements.action.value,
    target: linkValue.linkElements.target?.value,
    event: linkValue.linkElements.event?.value,
  });

  const linkUrl = linkProps && 'href' in linkProps && linkProps.href;
  if (!linkUrl && isPostMethod) {
    return null;
  }

  const handleSubmit = () => {
    pushInDataLayer({
      event: 'clickSignup',
      submittedFormData: {
        devops: linkText.toLowerCase(),
      },
    });
  };

  const WrapperComponent = isPostMethod
    ? ({ children }: { children: ReactNode }) => (
        <form
          name={`${linkText}-form`}
          action={linkUrl as string /* condition checked on line 26 */}
          onSubmit={handleSubmit}
          method="POST"
        >
          <Button type="submit" size={size}>
            {children}
          </Button>
        </form>
      )
    : ({ children }: { children: ReactNode }) => (
        <Button {...linkProps} title={linkText || undefined} size={size}>
          {children}
        </Button>
      );

  const iconHeight = size === 'large' ? LARGE_ICON_HEIGHT : SMALL_ICON_HEIGHT;
  const iconWidth = size === 'large' ? LARGE_ICON_WIDTH : SMALL_ICON_WIDTH;

  return (
    <li>
      <WrapperComponent>
        <>
          {linkChildren}
          <IconWrapper size={size}>
            <ImageElement
              alt={iconValue.description || undefined}
              image={{
                ...iconValue,
                height: iconValue.height ?? iconHeight,
                width: iconValue.width ?? iconWidth,
              }}
              objectFit="contain"
              title={iconValue.description || undefined}
            />
          </IconWrapper>
          {linkText}
        </>
      </WrapperComponent>
    </li>
  );
};

export default LoginLink;

export const fragment = graphql`
  fragment LOGIN_LINK on kontent_item_login_link {
    elements {
      link {
        value {
          ...LINK
        }
      }
      icon {
        value {
          description
          height
          type
          url
          width
        }
      }
      method {
        value
      }
    }
    system {
      id
    }
  }
`;
